import { motion } from "framer-motion";
import PageHero from "@/components/site/PageHero";
import { Eyebrow, Reveal, EASE } from "@/components/site/motion";
import { IMAGES, timeline } from "@/data/content";
import { useSEO } from "@/hooks/useSEO";

const chapters = [
  {
    num: "01",
    title: "Our Story",
    text: "Hapo Group began with Hapo Technology, a B2B technology company that has become a trusted go-to partner for enterprise technology solutions in South Africa. In 2025, Hapo Group was formed as a holding company, and eight new operating companies were established — Hapo Cloud, Hapo Media, Hapo Payment Solutions, Hapo Electronics, Hapo Energy, Hapo Intelligence, Hapo Sound and XR Africa — each a distinct, independently-run company.",
    image: IMAGES.joburg,
  },
  {
    num: "02",
    title: "Our Approach",
    text: "We believe South African businesses don't need to build every capability from scratch. Where a proven, world-class solution already exists elsewhere, we partner with it and bring it to market here — backed by our own manufacturing, technical delivery, and commercial execution.",
    image: IMAGES.network,
  },
  {
    num: "03",
    title: "Our Structure",
    text: "Each Hapo company is independently structured, with its own leadership and governance, under the Hapo Group holding company — built this way deliberately, for sound governance and long-term resilience.",
    image: IMAGES.building,
  },
];

export default function About() {
  useSEO(
    "About — Hapo Group",
    "Hapo Group began with Hapo Technology and was formed in 2025 as a holding company for six independent operating companies."
  );

  return (
    <>
      <PageHero
        short
        eyebrow="About Hapo Group"
        lines={["Built in South Africa.", "Built for What's Next."]}
        image={IMAGES.joburg}
      />

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[160px]" aria-label="Our story">
        {chapters.map((ch, i) => (
          <div
            key={ch.num}
            className={`grid items-center gap-10 py-16 md:grid-cols-2 lg:gap-20 lg:py-20 ${i > 0 ? "border-t border-ink/10" : ""}`}
          >
            <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
              <span className="text-6xl font-extrabold tracking-tighter text-gold lg:text-8xl">{ch.num}</span>
              <h2 className="mt-6 text-3xl font-bold tracking-tight text-ink lg:text-4xl">{ch.title}</h2>
              <p className="mt-5 max-w-lg text-sm leading-loose text-smoke md:text-base">{ch.text}</p>
            </Reveal>
            <Reveal delay={0.1} className={i % 2 === 1 ? "md:order-1" : ""}>
              <div className="overflow-hidden">
                <motion.img
                  initial={{ scale: 1.12 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: EASE }}
                  src={ch.image}
                  alt={`${ch.title} — placeholder image`}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
        ))}
      </section>

      <section className="bg-ink px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Milestones">
        <Eyebrow>Milestones</Eyebrow>
        <h2 className="mt-5 text-4xl font-extrabold tracking-tighter text-white sm:text-5xl">The road so far.</h2>
        <div className="mt-16 grid gap-12 border-t border-white/10 pt-14 md:grid-cols-3">
          {timeline.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.1}>
              <span className="text-5xl font-extrabold tracking-tighter text-gold">{m.num}</span>
              <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.24em] text-smoke">{m.when}</p>
              <h3 className="mt-2 text-xl font-bold text-white">{m.title}</h3>
              <p className="mt-3 text-sm leading-loose text-smoke">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
